import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { startOfDay, endOfDay } from "date-fns";
import { Util } from "../../util/Util";
import { useAppSocket } from "../../providers/AppSocketContext";
import { MIN_DATE } from "../../util/constants";
import { useTranslation } from "react-i18next";
import { DataScopeResource, summarizeDataScopes, useDataScopes } from "../../hooks/useDataScope";
import { useBusinessToday } from "../../hooks/useBusinessToday";
import { businessTodayDate, parseISODay } from "../../util/businessClock";
import { readStoredDateRange, writeStoredDateRange } from "../../util/sessionFilters";
import { FilterDateRange, sameDateRange } from "../../hooks/useDateRange";

import {
  DATE_RANGE_OPTION_KEYS as OPTION_KEYS,
  clampPresetRangeToScope,
  computePresetDateRange,
  normalizeDateRangeOption,
  type DateRangeOptionId,
} from "../../util/dateRangePresets";

interface Props {
  onChange: (from: Date | null, to: Date | null) => void;
  defaultOption?: string;
  /** Record types listed; bounds the picker by the user's read scope. */
  dataScope?: DataScopeResource | DataScopeResource[];
  /** Keeps the choice for the session under this key (the page's useData/useDateRange key). */
  storageKey?: string;
  /**
   * The page's current period. When it changes from outside (a cached period
   * restored, say) the filter shows it -- as the matching preset, or as custom
   * dates -- so what it shows is what the next refresh asks for.
   */
  value?: FilterDateRange | null;
}

const PRESET_IDS = OPTION_KEYS.map((opt) => opt.value).filter((id) => id !== "custom");

const storedDate = (value?: string | null): Date | null => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const DateRangeFilter = ({ onChange, defaultOption, dataScope, storageKey, value }: Props) => {
  const [stored] = useState(() => readStoredDateRange(storageKey));
  const [selected, setSelected] = useState<DateRangeOptionId>(
    stored?.preset || normalizeDateRangeOption(defaultOption) || "today",
  );
  const [customFrom, setCustomFrom] = useState<Date | null>(() => storedDate(stored?.from));
  const [customTo, setCustomTo] = useState<Date | null>(() => storedDate(stored?.to));
  // The default shown on mount is not a choice: storing it would hide a period
  // the page restores from its cache a moment later.
  const choiceMadeRef = useRef(Boolean(stored));
  const mountedRef = useRef(false);
  const { t } = useTranslation();
  const { appSettings } = useAppSocket();

  const scopes = useDataScopes();
  // Re-run presets when the server clock arrives (it may disagree with the browser's date).
  const businessDay = useBusinessToday();
  const scopeSummary = summarizeDataScopes(scopes, dataScope);
  const scopeMinDate = scopeSummary?.minDate ?? null;
  const inceptionDate = parseISODay(appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE) as Date;
  // The backend clamps reads to the scope window; mirror it so pickers and
  // presets never ask for dates the user cannot see.
  const minDate = scopeMinDate && scopeMinDate > inceptionDate ? scopeMinDate : inceptionDate;
  const scopeMinTime = scopeMinDate ? scopeMinDate.getTime() : null;

  const toPickerDate = (value: unknown): Date | null => {
    if (!value || Util.compareStrings(String(value), "Invalid Date")) return null;
    const date = value instanceof Date ? value : (value as { toDate: () => Date }).toDate();
    return date >= minDate ? date : null;
  };

  // Presets are business-calendar ranges ("today" = the server's today), clamped to the scope.
  const presetRange = (option: DateRangeOptionId): FilterDateRange => {
    const range = computePresetDateRange(option, businessTodayDate());
    return clampPresetRangeToScope(range, scopeMinDate);
  };
  const customRange = (from: Date | null, to: Date | null): FilterDateRange => ({
    from: from ? startOfDay(from) : null,
    to: to ? endOfDay(to) : null,
  });

  useEffect(() => {
    if (!choiceMadeRef.current) return;
    writeStoredDateRange(storageKey, {
      preset: selected,
      ...(selected === "custom" && { from: customFrom?.toISOString() ?? null, to: customTo?.toISOString() ?? null }),
    });
  }, [storageKey, selected, customFrom, customTo]);

  useEffect(() => {
    if (selected === "custom") return;
    const range = presetRange(selected);
    onChange(range.from, range.to);
  }, [selected, scopeMinTime, businessDay]); // onChange intentionally omitted — parent callbacks are often unstable

  useEffect(() => {
    if (selected !== "custom") return;
    const range = customRange(customFrom, customTo);
    onChange(range.from, range.to);
  }, [customFrom, customTo, selected]); // onChange intentionally omitted — parent callbacks are often unstable

  // Follow the page's period when it changes from outside. Not on mount: the
  // page has not heard from the filter yet, so its value is only a default.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (!value) return;
    const current = selected === "custom" ? customRange(customFrom, customTo) : presetRange(selected);
    if (sameDateRange(value, current)) return;
    choiceMadeRef.current = true;
    const preset = PRESET_IDS.find((id) => sameDateRange(value, presetRange(id)));
    if (preset) {
      setSelected(preset);
      return;
    }
    setCustomFrom(value.from);
    setCustomTo(value.to);
    setSelected("custom");
  }, [value?.from?.getTime(), value?.to?.getTime()]); // follows the period only, not every render of the page

  const handleSelectChange = (event: SelectChangeEvent) => {
    const option = event.target.value as DateRangeOptionId;
    choiceMadeRef.current = true;
    if (option === "custom") {
      setCustomFrom(null);
      setCustomTo(null);
    }
    setSelected(option);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ minWidth: 200 }}>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="date-range-label">{t("common:ui.labels.range")}</InputLabel>
            <Select
              labelId="date-range-label"
              value={selected}
              label={t("common:ui.labels.range")}
              onChange={handleSelectChange}
            >
              {OPTION_KEYS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.key)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {scopeSummary?.isRestricted && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1, whiteSpace: "pre-line" }}>
              {scopeSummary.parts
                ? scopeSummary.parts
                    .map((part) => {
                      const limits = [
                        part.scope !== "ALL" ? t(`common:dataScope.scope.${part.scope}`) : null,
                        part.bounded
                          ? t(`common:dataScope.period.${part.period}`, { count: part.days ?? 0 })
                          : null,
                      ].filter(Boolean);
                      return `${t(`common:dataScope.resource.${part.resource}`)}: ${
                        limits.length ? limits.join(" · ") : t("common:dataScope.unrestricted")
                      }`;
                    })
                    .join("\n")
                : [
                    scopeSummary.scope !== "ALL" ? t(`common:dataScope.scope.${scopeSummary.scope}`) : null,
                    scopeSummary.minDate
                      ? t(`common:dataScope.period.${scopeSummary.period}`, { count: scopeSummary.days ?? 0 })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
            </Typography>
          )}

          {selected === "custom" && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <DatePicker
                label={t("common:ui.labels.dateFrom")}
                value={customFrom}
                minDate={minDate}
                maxDate={customTo ?? undefined}
                onChange={(newDate) => {
                  if (!newDate) {
                    setCustomFrom(null);
                    return;
                  }
                  const date = toPickerDate(newDate);
                  if (!date) return;
                  setCustomFrom(date);
                  if (customTo && date > customTo) {
                    setCustomTo(null);
                  }
                }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <DatePicker
                label={t("common:ui.labels.dateTo")}
                value={customTo}
                minDate={customFrom ?? minDate}
                onChange={(newDate) => {
                  if (!newDate) {
                    setCustomTo(null);
                    return;
                  }
                  const date = toPickerDate(newDate);
                  if (!date) return;
                  setCustomTo(date);
                }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Stack>
          )}
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangeFilter;